/**
 * Meeting Autopilot, app side: reads the worker's results (Ready for you, the session page) and
 * runs the user's own actions on them (Undo, Change, rename, merge, per-user settings).
 *
 * Every call takes an optional `client` (the supabase client by default) so tests can pass a
 * fake. Reads tolerate columns that are not migrated yet (PostgREST 42703): the feature then
 * shows nothing instead of breaking the page. Nothing here logs customer data.
 */

import { supabase } from '../lib/supabase';
import { isMissingColumnError } from '../utils/supabaseSchemaFallback';
import {
  normalizeAutopilotRecord,
  normalizeAutopilotSettings,
  planUndo,
  requirementRowHash,
  toStoredAutopilotSettings,
  UUID_RE,
} from './autopilotModel';

/** ai_requests type of the worker job. */
export const AUTOPILOT_REQUEST_TYPE = 'meeting_autopilot';
/** Tables that make a project "in use" (besides the session being undone). */
const PROJECT_USAGE_TABLES = Object.freeze({
  sessions: 'sessions',
  documents: 'generated_documents',
  requirements: 'requirements',
  files: 'project_context_files',
  members: 'project_members',
});

/**
 * Error for a failed autopilot action, with a message safe to show.
 */
export class AutopilotActionError extends Error {
  /**
   * @param {string} message - Message safe to show.
   * @param {string} [code] - Machine code.
   */
  constructor(message, code = 'failed') {
    super(message);
    this.name = 'AutopilotActionError';
    this.code = code;
  }
}

/**
 * Whether a PostgREST error means a database function is not there yet (migration not applied).
 * @param {object|null} error - Error.
 * @returns {boolean} True for PGRST202 / 42883.
 */
function missingFunction(error) {
  const code = String(error?.code || '');
  return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(String(error?.message || ''));
}

/**
 * Whether a PostgREST error means the column is not there yet (any column).
 * @param {object|null} error - Error.
 * @param {string} column - Column name.
 * @returns {boolean} True for 42703 on that column.
 */
function missing(error, column) {
  return isMissingColumnError(error, column);
}

/**
 * Session id an ai_requests row is about.
 * @param {object} row - ai_requests row.
 * @returns {string|null} Session id.
 */
export function requestSessionId(row) {
  const fromData = (obj) => (obj && typeof obj === 'object' ? obj.sessionId || obj.session_id || null : null);
  const id = fromData(row?.request_data) || fromData(row?.result_data) || row?.session_id || null;
  return typeof id === 'string' && UUID_RE.test(id) ? id : null;
}

/**
 * The user's recent autopilot jobs, newest first, one per session.
 * @param {object} args - Arguments.
 * @param {string} args.userId - User id (ai_requests RLS shows only the caller's rows anyway).
 * @param {number} [args.limit] - Rows to read.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<Map<string, object>>} Session id to its latest job row.
 */
export async function loadAutopilotRequests({ userId, limit = 30, client = supabase }) {
  const bySession = new Map();
  if (!userId) return bySession;
  const { data, error } = await client
    .from('ai_requests')
    .select('id, status, request_data, result_data, progress_message, created_at, updated_at')
    .eq('user_id', userId)
    .eq('type', AUTOPILOT_REQUEST_TYPE)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  (data || []).forEach((row) => {
    const sessionId = requestSessionId(row);
    if (sessionId && !bySession.has(sessionId)) bySession.set(sessionId, row);
  });
  return bySession;
}

/**
 * Whether a run belongs in "Ready for you": still working, failed, or finished with something to
 * show (the worker's notify.inApp). Runs skipped because the flag was off or the session already
 * had a job are left out.
 * @param {object|null} record - Normalized record.
 * @returns {boolean} True to list it.
 */
export function isReadyForYouRecord(record) {
  if (!record) return false;
  if (record.status === 'queued' || record.status === 'running' || record.status === 'failed') return true;
  if (record.status === 'skipped' || record.status === 'cancelled') return false;
  return record.inApp !== false;
}

/**
 * Latest autopilot results for "Ready for you" (the signed-in user's own jobs; ai_requests RLS
 * shows only those), in the active workspace.
 * @param {object} args - Arguments.
 * @param {string} args.accountId - Active account.
 * @param {string} args.userId - Signed-in user.
 * @param {number} [args.limit] - Max rows.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<Array<{session: object, insights: object|null, record: object|null}>>} Newest first.
 */
export async function loadReadyForYou({ accountId, userId, limit = 6, client = supabase }) {
  if (!accountId || !userId) return [];
  const requests = await loadAutopilotRequests({ userId, client });
  const records = new Map();
  requests.forEach((row, sessionId) => {
    const record = normalizeAutopilotRecord(null, row);
    if (isReadyForYouRecord(record)) records.set(sessionId, record);
  });
  if (!records.size) return [];

  const { data: sessionRows, error } = await client
    .from('sessions')
    .select('id, name, project_id, account_id, created_at, source')
    .in('id', [...records.keys()]);
  if (error) throw error;

  const rows = (sessionRows || [])
    .filter((s) => s.account_id === accountId)
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .slice(0, limit);
  if (!rows.length) return [];

  const { data: insightRows, error: insightsError } = await client
    .from('session_insights')
    .select('*')
    .in('session_id', rows.map((s) => s.id));
  if (insightsError) throw insightsError;
  const insights = new Map((insightRows || []).map((r) => [r.session_id, r]));

  return rows.map((session) => ({ session, insights: insights.get(session.id) || null, record: records.get(session.id) }));
}

/**
 * The autopilot record of one session (null when autopilot never ran on it). The session's job
 * row is read directly (one per session); ai_requests RLS shows it to its owner only.
 * @param {object} args - Arguments.
 * @param {string} args.sessionId - Session id.
 * @param {string|null} args.userId - Signed-in user (for the job row).
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<object|null>} Normalized record.
 */
export async function loadSessionAutopilot({ sessionId, userId, client = supabase }) {
  if (!sessionId || !UUID_RE.test(String(sessionId)) || !userId) return null;
  const { data, error } = await client
    .from('ai_requests')
    .select('id, status, request_data, result_data, progress_message, created_at, updated_at')
    .eq('user_id', userId)
    .eq('type', AUTOPILOT_REQUEST_TYPE)
    .eq('request_data->>sessionId', sessionId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : null;
  return row ? normalizeAutopilotRecord(null, row) : null;
}

/**
 * Count rows of a table that point at a project.
 * @param {object} client - Supabase client.
 * @param {string} table - Table.
 * @param {string} projectId - Project id.
 * @returns {Promise<number>} Count.
 */
async function countFor(client, table, projectId) {
  const { count, error } = await client.from(table).select('project_id', { count: 'exact', head: true }).eq('project_id', projectId);
  if (error) throw error;
  return count || 0;
}

/**
 * Read a projects/customers row with its autopilot labels (labels absent before the migration).
 * @param {object} client - Supabase client.
 * @param {'projects'|'customers'} table - Table.
 * @param {string} id - Row id.
 * @param {string} columns - Base columns.
 * @returns {Promise<object|null>} Row.
 */
async function readLabelled(client, table, id, columns) {
  let { data, error } = await client.from(table).select(`${columns}, auto_created, auto_source`).eq('id', id).maybeSingle();
  if (error && (missing(error, 'auto_created') || missing(error, 'auto_source'))) {
    ({ data, error } = await client.from(table).select(columns).eq('id', id).maybeSingle());
  }
  if (error) throw error;
  return data || null;
}

/**
 * Count rows of a table that point at a project, leaving out the session being undone itself
 * (requirements are all counted; the caller subtracts the ones autopilot wrote).
 * @param {object} client - Supabase client.
 * @param {string} key - PROJECT_USAGE_TABLES key.
 * @param {string} projectId - Project id.
 * @param {string} sessionId - Session being undone.
 * @returns {Promise<number>} Count.
 */
async function usageFor(client, key, projectId, sessionId) {
  let query = client.from(PROJECT_USAGE_TABLES[key]).select('project_id', { count: 'exact', head: true }).eq('project_id', projectId);
  if (key === 'sessions') query = query.neq('id', sessionId);
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

/**
 * The requirement rows Autopilot's hidden extraction wrote for this session that nobody has
 * edited since: ids and content hashes come from the extraction job's result_data
 * (extraction.rows[{ id, hash }], written by the worker's processExtraction), and a row counts
 * as unedited only while its current content still has that hash. Rows a person extracted by
 * hand, or edited, never match.
 * @param {object} client - Supabase client.
 * @param {string} sessionId - Session id.
 * @param {string} projectId - Project the session was filed under.
 * @returns {Promise<{autopilotIds: string[], sessionRequirementCount: number}>} Unedited autopilot rows, and every requirement of the session in the project.
 */
async function autopilotRequirementsFor(client, sessionId, projectId) {
  const { data: sessionRows, error: rowsError } = await client
    .from('requirements')
    .select('id, title, description, type, priority, acceptance_criteria')
    .eq('session_id', sessionId)
    .eq('project_id', projectId);
  if (rowsError) throw rowsError;
  const rows = Array.isArray(sessionRows) ? sessionRows : [];
  if (rows.length === 0) return { autopilotIds: [], sessionRequirementCount: 0 };

  const { data: jobs, error: jobError } = await client
    .from('ai_requests')
    .select('id, status, request_data, result_data')
    .eq('type', 'requirement_extraction')
    .eq('request_data->>sessionId', sessionId)
    .eq('request_data->>source', 'meeting_autopilot')
    .limit(5);
  if (jobError) throw jobError;
  const written = new Map();
  for (const job of Array.isArray(jobs) ? jobs : []) {
    const extracted = job?.result_data?.extraction?.rows;
    if (!Array.isArray(extracted)) continue;
    for (const r of extracted) if (r && r.id && r.hash) written.set(String(r.id), String(r.hash));
  }
  const autopilotIds = rows.filter((r) => written.get(String(r.id)) === requirementRowHash(r)).map((r) => r.id);
  return { autopilotIds, sessionRequirementCount: rows.length };
}

/**
 * Undo autopilot's filing: move the session back to unfiled. When autopilot created the project
 * for this meeting and nothing else uses it, the project (with the unedited requirements
 * autopilot pulled into it) and the customer created with it are deleted. Otherwise every
 * requirement stays where it is (documents may have been built from them, or a person edited or
 * added them), the project is kept and, when autopilot made it, the result says so (the UI then
 * offers rename or merge).
 * @param {object} args - Arguments.
 * @param {string} args.sessionId - Session id.
 * @param {string} args.accountId - Active account.
 * @param {object} args.filing - Normalized filing (its projectId is where autopilot put it).
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<{status: 'undone'|'already_moved', deletedProject: boolean, deletedCustomer: boolean, deletedRequirements: number, keptRequirements: number, projectInUse: object|null, customerInUse: object|null}>} Outcome.
 * @throws {AutopilotActionError} When the session cannot be read or changed.
 */
export async function undoAutoFiling({ sessionId, accountId, filing, client = supabase }) {
  const none = { deletedProject: false, deletedCustomer: false, deletedRequirements: 0, keptRequirements: 0, projectInUse: null, customerInUse: null };
  if (!sessionId || !filing?.projectId) throw new AutopilotActionError('There is nothing to undo for this meeting.', 'nothing');

  const { data: session, error: sessionError } = await client
    .from('sessions')
    .select('id, project_id, account_id')
    .eq('id', sessionId)
    .maybeSingle();
  if (sessionError || !session) throw new AutopilotActionError('We could not find this meeting.', 'not_found');
  if (accountId && session.account_id && session.account_id !== accountId) {
    throw new AutopilotActionError('This meeting belongs to another workspace.', 'other_account');
  }
  if (session.project_id !== filing.projectId) return { status: 'already_moved', ...none };
  const projectId = session.project_id;

  // Work out what uses the project before anything changes.
  const project = await readLabelled(client, 'projects', projectId, 'id, name, customer_id, account_id');
  let usage;
  let mine;
  try {
    const usageEntries = await Promise.all(
      Object.keys(PROJECT_USAGE_TABLES).map(async (key) => [key, project ? await usageFor(client, key, projectId, sessionId) : 0])
    );
    usage = Object.fromEntries(usageEntries);
    mine = await autopilotRequirementsFor(client, sessionId, projectId);
  } catch {
    throw new AutopilotActionError('We could not check what uses this project. Nothing was changed.', 'usage');
  }
  // Every requirement in the project except the unedited ones autopilot wrote counts as use: a
  // person's own (with or without a meeting), edited ones and other meetings'.
  const projectUsage = { ...usage, requirements: Math.max(0, usage.requirements - mine.autopilotIds.length) };

  const { error: unlinkError } = await client.from('sessions').update({ project_id: null }).eq('id', sessionId);
  if (unlinkError) throw new AutopilotActionError('We could not move this meeting back to unfiled.', 'unlink');

  const customerId = project?.customer_id || null;
  const customer = customerId ? await readLabelled(client, 'customers', customerId, 'id, name, account_id') : null;
  let otherProjects = 0;
  if (customer) {
    const { count, error } = await client
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('customer_id', customer.id)
      .neq('id', projectId);
    if (error) throw error;
    otherProjects = count || 0;
  }

  const plan = planUndo({ sessionId, project, projectUsage, customer, customerUsage: { projects: otherProjects } });
  let deletedProject = false;
  let deletedCustomer = false;
  let deletedRequirements = 0;
  if (plan.deleteProject) {
    // Only the unedited rows autopilot wrote are left in a project nothing else uses.
    let cleared = true;
    if (mine.autopilotIds.length > 0) {
      const { data: removedReqs, error: reqError } = await client
        .from('requirements')
        .delete()
        .eq('session_id', sessionId)
        .eq('project_id', projectId)
        .in('id', mine.autopilotIds)
        .select('id');
      cleared = !reqError;
      deletedRequirements = reqError ? 0 : (removedReqs?.length || 0);
    }
    if (cleared) {
      // The hidden extraction job still points at the project; detach it so the delete is not
      // blocked by a reference (best effort: an older schema or policy may not allow it).
      try {
        await client
          .from('ai_requests')
          .update({ project_id: null })
          .eq('type', 'requirement_extraction')
          .eq('project_id', projectId)
          .eq('request_data->>sessionId', sessionId);
      } catch {
        // The project delete below reports whether it worked.
      }
      const { data: removed, error } = await client.from('projects').delete().eq('id', projectId).eq('account_id', accountId).select('id');
      deletedProject = !error && Array.isArray(removed) && removed.length > 0;
    }
  }
  if (plan.deleteCustomer && deletedProject) {
    const { data: removed, error } = await client.from('customers').delete().eq('id', customer.id).eq('account_id', accountId).select('id');
    deletedCustomer = !error && Array.isArray(removed) && removed.length > 0;
  }
  return {
    status: 'undone',
    deletedProject,
    deletedCustomer,
    deletedRequirements,
    keptRequirements: deletedProject ? 0 : mine.sessionRequirementCount,
    projectInUse: project && (plan.projectInUse || (plan.deleteProject && !deletedProject))
      ? { id: project.id, name: project.name, usage: projectUsage }
      : null,
    customerInUse: customer && (plan.customerInUse || (plan.deleteCustomer && !deletedCustomer))
      ? { id: customer.id, name: customer.name }
      : null,
  };
}

/**
 * File a session under another project ("Change").
 * @param {object} args - Arguments.
 * @param {string} args.sessionId - Session id.
 * @param {string} args.projectId - Target project.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<void>}
 * @throws {AutopilotActionError} On failure.
 */
export async function moveSessionToProject({ sessionId, projectId, client = supabase }) {
  if (!UUID_RE.test(String(sessionId || '')) || !UUID_RE.test(String(projectId || ''))) {
    throw new AutopilotActionError('Choose a project first.', 'invalid');
  }
  const { data, error } = await client.from('sessions').update({ project_id: projectId }).eq('id', sessionId).select('id');
  if (error || !data?.length) throw new AutopilotActionError('We could not move this meeting. Check you can edit that project.', 'move');
}

/**
 * Rename a project (keeps everything in it).
 * @param {object} args - Arguments.
 * @param {string} args.projectId - Project id.
 * @param {string} args.accountId - Active account.
 * @param {string} args.name - New name.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<void>}
 * @throws {AutopilotActionError} On failure.
 */
export async function renameProject({ projectId, accountId, name, client = supabase }) {
  const clean = String(name || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  if (!clean) throw new AutopilotActionError('Give the project a name.', 'invalid');
  const { data, error } = await client.from('projects').update({ name: clean }).eq('id', projectId).eq('account_id', accountId).select('id');
  if (error || !data?.length) throw new AutopilotActionError('We could not rename the project.', 'rename');
}

/**
 * Merge one project into another: its meetings, requirements and documents move, then the
 * emptied project is deleted. Projects with files or shared members are not merged (their
 * storage and sharing need a person to decide).
 * @param {object} args - Arguments.
 * @param {string} args.fromProjectId - Project to empty and delete.
 * @param {string} args.toProjectId - Project that keeps everything.
 * @param {string} args.accountId - Active account.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<{moved: {sessions: number, requirements: number, documents: number}}>} What moved.
 * @throws {AutopilotActionError} On failure or when the merge is not allowed.
 */
export async function mergeProjectInto({ fromProjectId, toProjectId, accountId, client = supabase }) {
  if (!fromProjectId || !toProjectId || fromProjectId === toProjectId) {
    throw new AutopilotActionError('Choose a different project to merge into.', 'invalid');
  }
  const [files, members] = await Promise.all([
    countFor(client, 'project_context_files', fromProjectId),
    countFor(client, 'project_members', fromProjectId),
  ]);
  if (files > 0 || members > 0) {
    throw new AutopilotActionError('This project has files or shared members, so it cannot be merged. Rename it instead.', 'has_files');
  }
  const moved = {};
  for (const [key, table] of [['sessions', 'sessions'], ['requirements', 'requirements'], ['documents', 'generated_documents']]) {
    const { data, error } = await client.from(table).update({ project_id: toProjectId }).eq('project_id', fromProjectId).select('id');
    if (error) throw new AutopilotActionError('The merge stopped part way. Some items may already be in the other project.', 'partial');
    moved[key] = data?.length || 0;
  }
  const { error } = await client.from('projects').delete().eq('id', fromProjectId).eq('account_id', accountId).select('id');
  if (error) throw new AutopilotActionError('Everything moved, but the empty project could not be deleted.', 'delete');
  return { moved };
}

/**
 * Read the signed-in user's Autopilot settings: public.get_autopilot_settings() (migration
 * 20261108000100; merges settings.autopilot with email_preferences.meeting_recaps, off wins).
 * Before the migration: the `settings` row (`autopilot` column, else `other_settings.autopilot`).
 * @param {object} args - Arguments.
 * @param {string} args.userId - User id.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<{autoSummaries: boolean, autoFiling: boolean, autoCreate: boolean, meetingRecaps: boolean}>} Settings.
 */
export async function loadAutopilotSettings({ userId, client = supabase }) {
  if (!userId) return normalizeAutopilotSettings(null);
  if (typeof client.rpc === 'function') {
    const { data, error } = await client.rpc('get_autopilot_settings');
    if (!error && data && typeof data === 'object' && !Array.isArray(data)) return normalizeAutopilotSettings(data);
    if (error && !missingFunction(error)) throw error;
  }
  const [settingsResult, emailResult] = await Promise.all([
    client.from('settings').select('*').eq('user_id', userId).maybeSingle(),
    client.from('email_preferences').select('*').eq('user_id', userId).maybeSingle(),
  ]);
  if (settingsResult.error && settingsResult.error.code !== 'PGRST116') throw settingsResult.error;
  const row = settingsResult.data || null;
  const raw = (row?.autopilot && typeof row.autopilot === 'object' ? row.autopilot : null)
    || (row?.other_settings && typeof row.other_settings === 'object' ? row.other_settings.autopilot : null);
  const settings = normalizeAutopilotSettings(raw);
  // Off wins, as in get_autopilot_settings.
  if (!emailResult?.error && emailResult?.data?.meeting_recaps === false) settings.meetingRecaps = false;
  return settings;
}

/**
 * Save the signed-in user's Autopilot settings (all four) with public.set_autopilot_settings,
 * which also writes email_preferences.meeting_recaps (the switch the recap email sender checks;
 * clients cannot write that table directly). Before the migration the choice is kept in the
 * settings row only (no recap emails are sent then).
 * @param {object} args - Arguments.
 * @param {string} args.userId - User id.
 * @param {object} args.settings - Settings to save.
 * @param {object} [args.client] - Supabase client.
 * @returns {Promise<{settings: object, recapsSynced: boolean}>} Saved settings (as the database now reads them).
 * @throws {AutopilotActionError} When the settings cannot be written.
 */
export async function saveAutopilotSettings({ userId, settings, client = supabase }) {
  if (!userId) throw new AutopilotActionError('Sign in to change Autopilot settings.', 'auth');
  const normalized = normalizeAutopilotSettings(settings);
  const stored = toStoredAutopilotSettings(normalized);

  if (typeof client.rpc === 'function') {
    const { data, error } = await client.rpc('set_autopilot_settings', { p_settings: stored });
    if (!error) {
      const saved = data && typeof data === 'object' && !Array.isArray(data) ? normalizeAutopilotSettings(data) : normalized;
      return { settings: saved, recapsSynced: true };
    }
    if (!missingFunction(error)) throw new AutopilotActionError('We could not save your Autopilot settings.', 'save');
  }

  let { error } = await client.from('settings').upsert({ user_id: userId, autopilot: stored }, { onConflict: 'user_id' });
  if (error && missing(error, 'autopilot')) {
    const { data: row, error: readError } = await client.from('settings').select('other_settings').eq('user_id', userId).maybeSingle();
    if (readError && readError.code !== 'PGRST116') throw new AutopilotActionError('We could not save your Autopilot settings.', 'save');
    const other = row?.other_settings && typeof row.other_settings === 'object' ? row.other_settings : {};
    ({ error } = await client
      .from('settings')
      .upsert({ user_id: userId, other_settings: { ...other, autopilot: stored } }, { onConflict: 'user_id' }));
  }
  if (error) throw new AutopilotActionError('We could not save your Autopilot settings.', 'save');
  return { settings: normalized, recapsSynced: false };
}
